import React from 'react';
import {
  Archive,
  Check,
  ChevronRight,
  CirclePlus,
  Download,
  EllipsisVertical,
  Plus,
  Search,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';
import { getImageUrl } from '@/utils/imageUrl';

import type { ContainerType } from '../../ContainerComponents/ContainerPage';
import type { FacetOption } from './ContainerFacetChip';
import {
  CORE_FACETS,
  EXTRA_FACETS,
  facetLabel,
  hasAnyFacetValues,
  type ContainerTypeFilterState,
  type FacetKey,
} from './containerTypeFilters';
import {
  EDITED_PILL_STYLE,
  getClassPillStyle,
  getSegmentDotColor,
  getSegmentIcon,
  WARNING_ACCENT,
} from './constants';

/** Facets promoted as always-visible chips on mobile. */
const PROMOTED_FACETS: FacetKey[] = ['class', 'group'];
const ALL_FACETS: FacetKey[] = [...CORE_FACETS, ...EXTRA_FACETS];

type MobileContainerTypesProps = {
  /** Loaded rows (grows via Load more), with pending edits applied. */
  rows: ContainerType[];
  /** Rows matching the active filters (across all pages). */
  totalCount: number;
  /** Rows in the archived partition regardless of other filters. */
  unfilteredCount: number;
  hasMore: boolean;
  onLoadMore: () => void;
  /** Optional feedback banner rendered under the header. */
  feedbackSlot?: React.ReactNode;
  filters: ContainerTypeFilterState;
  onFiltersChange: (next: ContainerTypeFilterState) => void;
  facetOptions: (key: FacetKey) => FacetOption[];
  dirtyIds: number[];
  displayName: (ct: ContainerType) => string;
  isAdministrator: boolean;
  isUpdating: boolean;
  saveDisabled: boolean;
  editorOpen: boolean;
  onOpen: (ct: ContainerType) => void;
  onNew: () => void;
  onExport: () => void;
  onSave: () => void;
  onDiscard: () => void;
};

/** Bottom sheet with scrim, grab handle and rounded top. */
function BottomSheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent
        side="bottom"
        showCloseButton={false}
        className="tw:max-h-[72dvh] tw:gap-0 tw:rounded-t-2xl tw:p-0"
      >
        <div className="tw:flex tw:max-h-[72dvh] tw:min-h-0 tw:flex-col tw:px-4 tw:pb-[max(24px,env(safe-area-inset-bottom))]">
          <span className="tw:mx-auto tw:mb-2.5 tw:mt-1.5 tw:h-1 tw:w-9 tw:flex-none tw:rounded-full tw:bg-border" />
          <SheetTitle className="tw:flex-none tw:pb-2.5 tw:text-sm tw:font-semibold">
            {title}
          </SheetTitle>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Mobile (<768px) presentation of the Container Types screen: wrapping filter
 * chips with bottom-sheet pickers, a two-column card grid and a floating save
 * bar. Same state and data as desktop — presentation only. This is a
 * responsive browser layout, not a native app shell.
 */
export default function MobileContainerTypes({
  rows,
  totalCount,
  unfilteredCount,
  hasMore,
  onLoadMore,
  feedbackSlot,
  filters,
  onFiltersChange,
  facetOptions,
  dirtyIds,
  displayName,
  isAdministrator,
  isUpdating,
  saveDisabled,
  editorOpen,
  onOpen,
  onNew,
  onExport,
  onSave,
  onDiscard,
}: MobileContainerTypesProps) {
  const [filterSheet, setFilterSheet] = React.useState<
    'picker' | FacetKey | null
  >(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  const update = (patch: Partial<ContainerTypeFilterState>) =>
    onFiltersChange({ ...filters, ...patch });

  const setFacet = (key: FacetKey, values: string[]) =>
    update({ facets: { ...filters.facets, [key]: values } });

  const facetSelection = (key: FacetKey) => filters.facets[key] ?? [];

  const anyFilter =
    filters.search !== '' || filters.showArchived || hasAnyFacetValues(filters);

  // Promoted chips always show; any other facet appears while it has values.
  const chipFacets = ALL_FACETS.filter(
    (key) => PROMOTED_FACETS.includes(key) || facetSelection(key).length > 0,
  );

  const chipValueLabel = (key: FacetKey): string => {
    const values = facetSelection(key);
    if (values.length === 1) {
      const option = facetOptions(key).find((o) => o.value === values[0]);
      return option?.label ?? values[0];
    }
    return i18n.t('Common:countSelected', { count: values.length });
  };

  const dirtyCount = dirtyIds.length;

  return (
    <div className="tw:flex tw:flex-col tw:gap-3.5 tw:p-4 tw:pb-28">
      {/* Header */}
      <div className="tw:flex tw:items-end tw:justify-between tw:gap-2.5">
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
          <h1 className="tw:text-[21px] tw:font-semibold tw:tracking-tight">
            {i18n.t('EditContainerInfo:pageTitle')}
          </h1>
          <span className="tw:text-[12.5px] tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:mobileCountLabel', {
              count: totalCount,
              total: unfilteredCount,
            })}
          </span>
        </div>
        <div className="tw:flex tw:flex-none tw:gap-2">
          {isAdministrator && (
            <Button
              size="icon"
              className="tw:size-[38px]"
              aria-label={i18n.t('EditContainerInfo:newContainerType')}
              onClick={onNew}
            >
              <Plus size={18} />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="tw:size-[38px]"
            aria-label={i18n.t('EditContainerInfo:moreActions')}
            onClick={() => setMenuOpen(true)}
          >
            <EllipsisVertical size={17} />
          </Button>
        </div>
      </div>

      {feedbackSlot}

      {/* Search */}
      <div className="tw:relative">
        <Search
          size={16}
          className="tw:pointer-events-none tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-muted-foreground"
        />
        <input
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          placeholder={i18n.t('EditContainerInfo:searchPlaceholderShort')}
          className="tw:h-10 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-background tw:pl-9 tw:pr-3 tw:text-[15px] tw:text-foreground tw:shadow-xs tw:outline-none"
        />
      </div>

      {/* Filter chips (wrap — no horizontal scrolling) */}
      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
        {chipFacets.map((key) => {
          const values = facetSelection(key);
          const active = values.length > 0;
          return (
            <div
              key={key}
              className={cn(
                'tw:inline-flex tw:h-[34px] tw:items-center tw:rounded-md',
                active
                  ? 'tw:border tw:border-border tw:bg-background tw:shadow-xs'
                  : 'tw:border tw:border-dashed tw:border-border tw:bg-transparent',
              )}
            >
              <button
                type="button"
                onClick={() => setFilterSheet(key)}
                className={cn(
                  'tw:inline-flex tw:h-full tw:items-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-md tw:border-none tw:bg-transparent tw:text-[12.5px] tw:font-medium tw:outline-none tw:cursor-pointer',
                  active
                    ? 'tw:pl-3 tw:pr-2 tw:text-foreground'
                    : 'tw:px-3 tw:text-muted-foreground',
                )}
              >
                {!active && <CirclePlus size={13} />}
                {facetLabel(key)}
                {active && (
                  <span className="tw:inline-flex tw:h-5 tw:max-w-[110px] tw:items-center tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap tw:rounded-[6px] tw:bg-muted tw:px-1.5 tw:text-[11.5px] tw:font-semibold tw:text-foreground">
                    {chipValueLabel(key)}
                  </span>
                )}
              </button>
              {active && (
                <button
                  type="button"
                  aria-label={i18n.t('EditContainerInfo:clearFacet', {
                    facet: facetLabel(key),
                  })}
                  onClick={() => setFacet(key, [])}
                  className="tw:inline-flex tw:h-full tw:w-[30px] tw:items-center tw:justify-center tw:rounded-r-md tw:border-l tw:border-border tw:bg-transparent tw:text-muted-foreground tw:cursor-pointer"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => setFilterSheet('picker')}
          className="tw:inline-flex tw:h-[34px] tw:items-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-md tw:border-none tw:bg-muted tw:px-3 tw:text-[12.5px] tw:font-medium tw:text-foreground tw:outline-none tw:cursor-pointer"
        >
          <Plus size={14} />
          {i18n.t('EditContainerInfo:addAFilter')}
        </button>
        <button
          type="button"
          onClick={() => update({ showArchived: !filters.showArchived })}
          className={cn(
            'tw:inline-flex tw:h-[34px] tw:items-center tw:gap-1.5 tw:whitespace-nowrap tw:rounded-md tw:px-2.5 tw:text-[12.5px] tw:font-medium tw:outline-none tw:cursor-pointer',
            filters.showArchived
              ? 'tw:border tw:border-border tw:bg-background tw:text-foreground tw:shadow-xs'
              : 'tw:border tw:border-dashed tw:border-border tw:bg-transparent tw:text-muted-foreground',
          )}
        >
          <Archive size={13} />
          {i18n.t('Common:archived')}
        </button>
        {anyFilter && (
          <button
            type="button"
            onClick={() =>
              update({
                search: '',
                showArchived: false,
                facets: {},
                extraFacets: [],
              })
            }
            className="tw:inline-flex tw:h-[34px] tw:items-center tw:gap-1 tw:rounded-md tw:border-none tw:bg-transparent tw:px-2.5 tw:text-[12.5px] tw:font-medium tw:text-muted-foreground tw:outline-none tw:cursor-pointer"
          >
            <X size={12} />
            {i18n.t('EditContainerInfo:clear')}
          </button>
        )}
      </div>

      {/* Card grid */}
      {rows.length === 0 ? (
        <div className="tw:py-16 tw:text-center tw:text-sm tw:text-muted-foreground">
          {i18n.t('EditContainerInfo:emptyState')}
        </div>
      ) : (
        <div className="tw:grid tw:grid-cols-2 tw:gap-2.5">
          {rows.map((ct) => {
            const dirty = dirtyIds.includes(ct.id);
            const classStyle = getClassPillStyle(ct.class);
            const SegmentIcon = getSegmentIcon(ct.segment);
            return (
              <button
                key={ct.id}
                type="button"
                onClick={() => onOpen(ct)}
                className="tw:flex tw:cursor-pointer tw:flex-col tw:items-stretch tw:overflow-hidden tw:rounded-lg tw:border tw:bg-background tw:p-0 tw:text-left tw:outline-none"
                style={{
                  borderColor: dirty
                    ? `color-mix(in oklab, ${WARNING_ACCENT} 55%, var(--border))`
                    : 'var(--border)',
                }}
              >
                {/* Static 9:4 media ratio (the design's 174x78) — the height
                    scales with the card width instead of staying 78px. */}
                <div className="tw:relative tw:flex tw:aspect-[9/4] tw:w-full tw:items-center tw:justify-center tw:border-b tw:border-border tw:bg-muted tw:text-muted-foreground">
                  {ct.imageUrl ? (
                    <img
                      src={getImageUrl(480, ct.imageUrl)}
                      alt=""
                      className="tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-cover"
                    />
                  ) : (
                    <SegmentIcon size={30} strokeWidth={1.75} />
                  )}
                  {ct.hexCode && (
                    <div
                      className="tw:pointer-events-none tw:absolute tw:inset-0 tw:opacity-30"
                      style={{ backgroundColor: `#${ct.hexCode}` }}
                    />
                  )}
                  {ct.class && (
                    <span
                      className="tw:absolute tw:left-[7px] tw:top-[7px] tw:inline-flex tw:h-[18px] tw:items-center tw:whitespace-nowrap tw:rounded-full tw:px-1.5 tw:text-[9.5px] tw:font-semibold"
                      style={{
                        backgroundColor: classStyle.backgroundColor,
                        color: classStyle.color,
                        border: classStyle.border,
                      }}
                    >
                      {ct.class}
                    </span>
                  )}
                  {ct.sizeHelper && (
                    <span className="tw:absolute tw:right-[7px] tw:top-[7px] tw:inline-flex tw:h-[19px] tw:min-w-5 tw:items-center tw:justify-center tw:rounded-[6px] tw:border tw:border-border tw:bg-background tw:px-1.5 tw:text-[10.5px] tw:font-semibold">
                      {ct.sizeHelper}
                    </span>
                  )}
                  {dirty && (
                    <span
                      className="tw:absolute tw:bottom-[7px] tw:right-[7px] tw:inline-flex tw:h-4 tw:items-center tw:rounded-full tw:px-1.5 tw:text-[9px] tw:font-bold tw:uppercase tw:tracking-wide"
                      style={{
                        backgroundColor: EDITED_PILL_STYLE.backgroundColor,
                        color: EDITED_PILL_STYLE.color,
                        border: EDITED_PILL_STYLE.border,
                      }}
                    >
                      {i18n.t('EditContainerInfo:edited')}
                    </span>
                  )}
                </div>
                <div className="tw:flex tw:w-full tw:flex-col tw:gap-1 tw:px-2.5 tw:pb-2.5 tw:pt-2">
                  <span className="tw:line-clamp-2 tw:min-h-8 tw:text-[12.5px] tw:font-medium tw:leading-tight">
                    {displayName(ct)}
                  </span>
                  <span className="tw:flex tw:items-center tw:justify-between tw:gap-1.5">
                    <span className="tw:truncate tw:font-mono tw:text-[10.5px] tw:text-muted-foreground">
                      {ct.sku || '—'}
                    </span>
                    {ct.segment && (
                      <span
                        className="tw:inline-flex tw:size-[7px] tw:flex-none tw:rounded-full"
                        style={{
                          backgroundColor: getSegmentDotColor(ct.segment),
                        }}
                      />
                    )}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Load more (server pagination) */}
      {hasMore && (
        <Button
          variant="outline"
          className="tw:h-10 tw:w-full"
          onClick={onLoadMore}
        >
          {i18n.t('EditContainerInfo:loadMore')}
        </Button>
      )}

      {/* Floating save bar (list only) */}
      {dirtyCount > 0 && !editorOpen && (
        <div className="tw:fixed tw:bottom-[max(24px,env(safe-area-inset-bottom))] tw:left-3.5 tw:right-3.5 tw:z-40 tw:flex tw:items-center tw:gap-2.5 tw:rounded-full tw:border tw:border-border tw:bg-popover tw:py-2 tw:pl-3.5 tw:pr-2 tw:shadow-lg">
          <span
            className="tw:inline-flex tw:size-2 tw:flex-none tw:rounded-full"
            style={{ backgroundColor: WARNING_ACCENT }}
          />
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-[13px] tw:font-medium">
            {i18n.t('EditContainerInfo:nUnsaved', { count: dirtyCount })}
          </span>
          <Button variant="ghost" size="sm" onClick={onDiscard}>
            {i18n.t('Common:discard')}
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saveDisabled || isUpdating}
          >
            <Check size={14} />
            {i18n.t('Common:save')}
          </Button>
        </div>
      )}

      {/* Add-a-filter picker sheet */}
      <BottomSheet
        open={filterSheet === 'picker'}
        onClose={() => setFilterSheet(null)}
        title={i18n.t('EditContainerInfo:addAFilter')}
      >
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
          {ALL_FACETS.map((key) => {
            const count = facetSelection(key).length;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFilterSheet(key)}
                className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:border-b tw:border-border tw:bg-transparent tw:px-1 tw:py-3 tw:text-left tw:text-[14.5px] tw:text-foreground tw:outline-none tw:cursor-pointer"
              >
                <span className="tw:flex-1">{facetLabel(key)}</span>
                {count > 0 && (
                  <span className="tw:inline-flex tw:h-[18px] tw:min-w-[18px] tw:items-center tw:justify-center tw:rounded-[6px] tw:bg-primary tw:px-1.5 tw:text-[11px] tw:font-bold tw:text-primary-foreground">
                    {count}
                  </span>
                )}
                <ChevronRight size={16} className="tw:text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </BottomSheet>

      {/* Facet value sheet */}
      <BottomSheet
        open={filterSheet !== null && filterSheet !== 'picker'}
        onClose={() => setFilterSheet(null)}
        title={
          filterSheet && filterSheet !== 'picker'
            ? i18n.t('EditContainerInfo:filterByFacet', {
                facet: facetLabel(filterSheet),
              })
            : ''
        }
      >
        {filterSheet && filterSheet !== 'picker' && (
          <>
            <div className="tw:min-h-0 tw:flex-1 tw:overflow-auto">
              {facetOptions(filterSheet).map((option) => {
                const values = facetSelection(filterSheet);
                const checked = values.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFacet(
                        filterSheet,
                        checked
                          ? values.filter((v) => v !== option.value)
                          : [...values, option.value],
                      )
                    }
                    className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:border-b tw:border-border tw:bg-transparent tw:px-1 tw:py-2.5 tw:text-left tw:text-[14.5px] tw:text-foreground tw:outline-none tw:cursor-pointer"
                  >
                    <span
                      className={cn(
                        'tw:inline-flex tw:size-[18px] tw:flex-none tw:items-center tw:justify-center tw:rounded-[5px] tw:border',
                        checked
                          ? 'tw:border-primary tw:bg-primary tw:text-primary-foreground'
                          : 'tw:border-input tw:bg-background tw:text-transparent',
                      )}
                    >
                      <Check size={12} />
                    </span>
                    <span className="tw:truncate">{option.label}</span>
                  </button>
                );
              })}
              {facetOptions(filterSheet).length === 0 && (
                <div className="tw:px-1 tw:py-4 tw:text-sm tw:text-muted-foreground">
                  {i18n.t('Common:noResultsFound')}
                </div>
              )}
            </div>
            <div className="tw:flex-none tw:pt-3">
              <Button
                className="tw:h-10 tw:w-full"
                onClick={() => setFilterSheet(null)}
              >
                {i18n.t('Common:done')}
              </Button>
            </div>
          </>
        )}
      </BottomSheet>

      {/* Actions sheet (⋯) */}
      <BottomSheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={i18n.t('EditContainerInfo:actions')}
      >
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            onExport();
          }}
          className="tw:flex tw:w-full tw:items-center tw:gap-3 tw:bg-transparent tw:px-1 tw:py-3 tw:text-left tw:text-[14.5px] tw:text-foreground tw:outline-none tw:cursor-pointer"
        >
          <Download size={17} className="tw:text-muted-foreground" />
          {i18n.t('EditContainerInfo:exportContainerTypes')}
        </button>
      </BottomSheet>
    </div>
  );
}
